import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Modal from '../../components/UI/Modal';
import { surveyAPI } from '../../services/api';
import { 
  Plus, Edit, Trash2, Eye, Power, PowerOff, 
  GripVertical, X, PlusCircle, Save, FileText 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const Container = styled.div`
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
`;

const SurveyCard = styled(Card)`
  padding: 1.5rem;
  margin-bottom: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    box-shadow: ${props => props.theme.shadows.lg};
  }
`;

const SurveyHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 1rem;
`;

const SurveyTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
`;

const SurveyActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  background-color: ${props => props.isActive ? '#10B981' : '#EF4444'};
  color: white;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.typography.fontSize.xl};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid ${props => props.theme.colors.border.primary};
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  font-size: ${props => props.theme.typography.fontSize.base};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  font-size: ${props => props.theme.typography.fontSize.base};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const QuestionsList = styled.div`
  margin-top: 1.5rem;
`;

const QuestionItem = styled.div`
  display: flex;
  align-items: start;
  gap: 1rem;
  padding: 1rem;
  background-color: ${props => props.theme.colors.background.secondary};
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: 1rem;
`;

const QuestionContent = styled.div`
  flex: 1;
`;

const QuestionText = styled.div`
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  margin-bottom: 0.5rem;
`;

const QuestionMeta = styled.div`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
`;

const OptionsList = styled.div`
  margin-top: 0.5rem;
  padding-left: 1rem;
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
`;

const SurveyManagement = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [currentSurvey, setCurrentSurvey] = useState(null);
  
  const [surveyData, setSurveyData] = useState({
    title: '',
    description: '',
    instructions: '',
    is_active: false,
    start_date: '',
    end_date: '',
    allow_multiple_submissions: false,
    require_authentication: false,
    show_progress: true,
    display_order: 0,
    collect_respondent_info: false,
    require_name: false,
    require_email: false,
    require_phone: false,
    collect_name: true,
    collect_email: true,
    collect_phone: false,
    custom_respondent_fields: [],
  });
  
  const [newCustomField, setNewCustomField] = useState({
    name: '',
    label: '',
    type: 'text',
    required: false,
    options: [],
  });
  
  const [newOption, setNewOption] = useState('');
  
  const [questionData, setQuestionData] = useState({
    question_text: '',
    question_type: 'text',
    options: [],
    is_required: false,
    help_text: '',
    display_order: 0,
  });
  
  const [questionOption, setQuestionOption] = useState('');

  useEffect(() => {
    fetchSurveys();
  }, []);

  const fetchSurveys = async () => {
    try {
      setLoading(true);
      const response = await surveyAPI.getAll();
      setSurveys(response.data.data || response.data || []);
    } catch (error) {
      console.error('Error fetching surveys:', error);
      toast.error('Failed to load surveys');
    } finally {
      setLoading(false);
    }
  };

  const fetchSurveyDetails = async (id) => {
    try {
      const response = await surveyAPI.getById(id);
      setCurrentSurvey(response.data);
      setSurveyData({
        title: response.data.title || '',
        description: response.data.description || '',
        instructions: response.data.instructions || '',
        is_active: response.data.is_active || false,
        start_date: response.data.start_date ? response.data.start_date.split('T')[0] : '',
        end_date: response.data.end_date ? response.data.end_date.split('T')[0] : '',
        allow_multiple_submissions: response.data.allow_multiple_submissions || false,
        require_authentication: response.data.require_authentication || false,
        show_progress: response.data.show_progress !== false,
        display_order: response.data.display_order || 0,
        collect_respondent_info: response.data.collect_respondent_info || false,
        require_name: response.data.require_name || false,
        require_email: response.data.require_email || false,
        require_phone: response.data.require_phone || false,
        collect_name: response.data.collect_name !== false,
        collect_email: response.data.collect_email !== false,
        collect_phone: response.data.collect_phone || false,
        custom_respondent_fields: response.data.custom_respondent_fields || [],
      });
      setEditingSurvey(id);
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching survey:', error);
      toast.error('Failed to load survey details');
    }
  };

  const handleSaveSurvey = async () => {
    try {
      const data = {
        ...surveyData,
        start_date: surveyData.start_date || null,
        end_date: surveyData.end_date || null,
      };
      
      if (editingSurvey) {
        await surveyAPI.update(editingSurvey, data);
        toast.success('Survey updated successfully');
        // Refresh survey details to show updated info
        await fetchSurveyDetails(editingSurvey);
      } else {
        const newSurvey = await surveyAPI.create(data);
        toast.success('Survey created successfully');
        // Set as editing survey and fetch details so user can add questions
        setEditingSurvey(newSurvey.data.id);
        await fetchSurveyDetails(newSurvey.data.id);
        setShowModal(true); // Keep modal open so user can add questions
      }
      
      // Don't close modal if it's a new survey - let user add questions
      if (editingSurvey) {
        // Keep modal open if there are no questions yet
        if (!currentSurvey || !currentSurvey.questions || currentSurvey.questions.length === 0) {
          // Modal stays open
        } else {
          // User can close manually if they want
        }
      }
      // Only reset if modal is being closed
      // Don't reset if it's a new survey being created (user should add questions)
      if (!editingSurvey || (currentSurvey && currentSurvey.questions && currentSurvey.questions.length > 0)) {
        setShowModal(false);
        setEditingSurvey(null);
        setCurrentSurvey(null);
        setSurveyData({
          title: '',
          description: '',
          instructions: '',
          is_active: false,
          start_date: '',
          end_date: '',
          allow_multiple_submissions: false,
          require_authentication: false,
          show_progress: true,
          display_order: 0,
        });
        fetchSurveys();
      }
    } catch (error) {
      console.error('Error saving survey:', error);
      toast.error('Failed to save survey');
    }
  };

  const handleDeleteSurvey = async (id) => {
    if (window.confirm('Are you sure you want to delete this survey?')) {
      try {
        await surveyAPI.delete(id);
        toast.success('Survey deleted successfully');
        fetchSurveys();
      } catch (error) {
        console.error('Error deleting survey:', error);
        toast.error('Failed to delete survey');
      }
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await surveyAPI.toggleActive(id);
      toast.success(`Survey ${!currentStatus ? 'activated' : 'deactivated'}`);
      fetchSurveys();
    } catch (error) {
      console.error('Error toggling survey status:', error);
      toast.error('Failed to update survey status');
    }
  };

  const handleAddQuestion = async () => {
    if (!editingSurvey) {
      toast.error('Please save the survey first before adding questions');
      return;
    }
    
    try {
      const data = {
        ...questionData,
        display_order: currentSurvey?.questions?.length || 0,
      };
      
      await surveyAPI.addQuestion(editingSurvey, data);
      toast.success('Question added successfully');
      setShowQuestionModal(false);
      setQuestionData({
        question_text: '',
        question_type: 'text',
        options: [],
        is_required: false,
        help_text: '',
        display_order: 0,
      });
      fetchSurveyDetails(editingSurvey);
    } catch (error) {
      console.error('Error adding question:', error);
      toast.error('Failed to add question');
    }
  };

  const handleUpdateQuestion = async () => {
    try {
      await surveyAPI.updateQuestion(editingQuestion.id, questionData);
      toast.success('Question updated successfully');
      setShowQuestionModal(false);
      setEditingQuestion(null);
      fetchSurveyDetails(editingSurvey);
    } catch (error) {
      console.error('Error updating question:', error);
      toast.error('Failed to update question');
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      try {
        await surveyAPI.deleteQuestion(questionId);
        toast.success('Question deleted successfully');
        fetchSurveyDetails(editingSurvey);
      } catch (error) {
        console.error('Error deleting question:', error);
        toast.error('Failed to delete question');
      }
    }
  };

  const addOption = () => {
    if (questionOption.trim()) {
      setQuestionData(prev => ({
        ...prev,
        options: [...(prev.options || []), questionOption.trim()]
      }));
      setQuestionOption('');
    }
  };

  const removeOption = (index) => {
    setQuestionData(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const openQuestionModal = (question = null) => {
    if (question) {
      setEditingQuestion(question);
      setQuestionData({
        question_text: question.question_text || '',
        question_type: question.question_type || 'text',
        options: question.options || [],
        is_required: question.is_required || false,
        help_text: question.help_text || '',
        display_order: question.display_order || 0,
      });
    } else {
      setEditingQuestion(null);
      setQuestionData({
        question_text: '',
        question_type: 'text',
        options: [],
        is_required: false,
        help_text: '',
        display_order: currentSurvey?.questions?.length || 0,
      });
    }
    setQuestionOption(''); // Reset question option input
    setShowQuestionModal(true);
  };

  return (
    <Container>
      <Header>
        <Title theme={theme}>Survey Management</Title>
        <Button 
          variant="primary" 
          leftIcon={<Plus size={16} />}
          onClick={() => {
            setEditingSurvey(null);
            setCurrentSurvey(null);
            setSurveyData({
              title: '',
              description: '',
              instructions: '',
              is_active: false,
              start_date: '',
              end_date: '',
              allow_multiple_submissions: false,
              require_authentication: false,
              show_progress: true,
              display_order: 0,
              collect_respondent_info: false,
              require_name: false,
              require_email: false,
              require_phone: false,
              collect_name: true,
              collect_email: true,
              collect_phone: false,
              custom_respondent_fields: [],
            });
            setShowModal(true);
          }}
        >
          Create Survey
        </Button>
      </Header>

      {loading ? (
        <div>Loading surveys...</div>
      ) : surveys.length === 0 ? (
        <Card padding="2rem" textAlign="center">
          <FileText size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
          <p>No surveys yet. Create your first survey to get started.</p>
        </Card>
      ) : (
        surveys.map(survey => (
          <SurveyCard key={survey.id} theme={theme}>
            <SurveyHeader>
              <div style={{ flex: 1 }}>
                <SurveyTitle theme={theme}>{survey.title}</SurveyTitle>
                <div style={{ marginTop: '0.5rem', fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                  {survey.description}
                </div>
                <div style={{ marginTop: '0.5rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <StatusBadge isActive={survey.is_active}>
                    {survey.is_active ? 'Active' : 'Inactive'}
                  </StatusBadge>
                  <span style={{ fontSize: theme.typography.fontSize.xs }}>
                    {survey.questions?.length || 0} questions
                  </span>
                </div>
              </div>
              <SurveyActions>
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={survey.is_active ? <PowerOff size={16} /> : <Power size={16} />}
                  onClick={() => handleToggleActive(survey.id, survey.is_active)}
                >
                  {survey.is_active ? 'Deactivate' : 'Activate'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Eye size={16} />}
                  onClick={() => navigate(`/app/surveys/${survey.id}/responses`)}
                >
                  View Responses
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Edit size={16} />}
                  onClick={() => fetchSurveyDetails(survey.id)}
                >
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Trash2 size={16} />}
                  onClick={() => handleDeleteSurvey(survey.id)}
                >
                  Delete
                </Button>
              </SurveyActions>
            </SurveyHeader>
          </SurveyCard>
        ))
      )}

      {/* Survey Form Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingSurvey(null);
          setCurrentSurvey(null);
        }}
        title={editingSurvey ? 'Edit Survey' : 'Create Survey'}
        size="large"
      >
        <FormGroup theme={theme}>
          <Label theme={theme}>Title *</Label>
          <Input
            value={surveyData.title}
            onChange={(e) => setSurveyData(prev => ({ ...prev, title: e.target.value }))}
            placeholder="Survey Title"
            required
          />
        </FormGroup>

        <FormGroup theme={theme}>
          <Label theme={theme}>Description</Label>
          <Textarea
            value={surveyData.description}
            onChange={(e) => setSurveyData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Survey description"
            rows={3}
          />
        </FormGroup>

        <FormGroup theme={theme}>
          <Label theme={theme}>Instructions</Label>
          <Textarea
            value={surveyData.instructions}
            onChange={(e) => setSurveyData(prev => ({ ...prev, instructions: e.target.value }))}
            placeholder="Instructions for users taking this survey"
            rows={3}
          />
        </FormGroup>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <FormGroup theme={theme}>
            <Label theme={theme}>Start Date</Label>
            <Input
              type="date"
              value={surveyData.start_date}
              onChange={(e) => setSurveyData(prev => ({ ...prev, start_date: e.target.value }))}
            />
          </FormGroup>

          <FormGroup theme={theme}>
            <Label theme={theme}>End Date</Label>
            <Input
              type="date"
              value={surveyData.end_date}
              onChange={(e) => setSurveyData(prev => ({ ...prev, end_date: e.target.value }))}
            />
          </FormGroup>
        </div>

        <FormGroup theme={theme}>
          <Label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={surveyData.allow_multiple_submissions}
              onChange={(e) => setSurveyData(prev => ({ ...prev, allow_multiple_submissions: e.target.checked }))}
            />
            Allow Multiple Submissions
          </Label>
        </FormGroup>

        <FormGroup theme={theme}>
          <Label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={surveyData.require_authentication}
              onChange={(e) => setSurveyData(prev => ({ ...prev, require_authentication: e.target.checked }))}
            />
            Require Authentication
          </Label>
        </FormGroup>

        {/* Respondent Information Section */}
        <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: `2px solid ${theme.colors.primary}` }}>
          <SectionTitle theme={theme}>Respondent Information Collection</SectionTitle>
          <p style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary, marginBottom: '1.5rem' }}>
            Configure what information to collect from respondents before they take the survey.
          </p>

          <FormGroup theme={theme}>
            <Label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={surveyData.collect_respondent_info}
                onChange={(e) => setSurveyData(prev => ({ ...prev, collect_respondent_info: e.target.checked }))}
              />
              Collect Respondent Information
            </Label>
          </FormGroup>

          {surveyData.collect_respondent_info && (
            <div style={{ paddingLeft: '1.5rem', marginTop: '1rem' }}>
              <FormGroup theme={theme}>
                <Label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={surveyData.collect_name}
                    onChange={(e) => setSurveyData(prev => ({ ...prev, collect_name: e.target.checked, require_name: e.target.checked ? prev.require_name : false }))}
                  />
                  Collect Name
                </Label>
                {surveyData.collect_name && (
                  <Label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={surveyData.require_name}
                      onChange={(e) => setSurveyData(prev => ({ ...prev, require_name: e.target.checked }))}
                    />
                    Required
                  </Label>
                )}
              </FormGroup>

              <FormGroup theme={theme}>
                <Label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={surveyData.collect_email}
                    onChange={(e) => setSurveyData(prev => ({ ...prev, collect_email: e.target.checked, require_email: e.target.checked ? prev.require_email : false }))}
                  />
                  Collect Email
                </Label>
                {surveyData.collect_email && (
                  <Label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={surveyData.require_email}
                      onChange={(e) => setSurveyData(prev => ({ ...prev, require_email: e.target.checked }))}
                    />
                    Required
                  </Label>
                )}
              </FormGroup>

              <FormGroup theme={theme}>
                <Label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={surveyData.collect_phone}
                    onChange={(e) => setSurveyData(prev => ({ ...prev, collect_phone: e.target.checked, require_phone: e.target.checked ? prev.require_phone : false }))}
                  />
                  Collect Phone Number
                </Label>
                {surveyData.collect_phone && (
                  <Label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={surveyData.require_phone}
                      onChange={(e) => setSurveyData(prev => ({ ...prev, require_phone: e.target.checked }))}
                    />
                    Required
                  </Label>
                )}
              </FormGroup>

              {/* Custom Fields */}
              <div style={{ marginTop: '2rem' }}>
                <Label theme={theme}>Custom Respondent Fields (e.g., Gender, Age, Location)</Label>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                  <Input
                    value={newCustomField.label}
                    onChange={(e) => setNewCustomField(prev => ({ ...prev, label: e.target.value, name: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                    placeholder="Field Label (e.g., Gender, Age)"
                    style={{ flex: 1 }}
                  />
                  <Select
                    theme={theme}
                    value={newCustomField.type}
                    onChange={(e) => setNewCustomField(prev => ({ ...prev, type: e.target.value, options: e.target.value === 'select' ? prev.options : [] }))}
                    style={{ width: '150px' }}
                  >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="email">Email</option>
                    <option value="date">Date</option>
                    <option value="select">Dropdown</option>
                  </Select>
                  {newCustomField.type === 'select' && (
                    <div style={{ display: 'flex', gap: '0.5rem', width: '200px' }}>
                      <Input
                        value={newOption}
                        onChange={(e) => setNewOption(e.target.value)}
                        placeholder="Option value"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && newOption.trim()) {
                            setNewCustomField(prev => ({
                              ...prev,
                              options: [...(prev.options || []), newOption.trim()]
                            }));
                            setNewOption('');
                          }
                        }}
                      />
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => {
                          if (newOption.trim()) {
                            setNewCustomField(prev => ({
                              ...prev,
                              options: [...(prev.options || []), newOption.trim()]
                            }));
                            setNewOption('');
                          }
                        }}
                      >
                        Add Option
                      </Button>
                    </div>
                  )}
                  {newCustomField.type === 'select' && newCustomField.options?.length > 0 && (
                    <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                      {newCustomField.options.map((opt, idx) => (
                        <span key={idx} style={{ 
                          padding: '0.25rem 0.5rem', 
                          background: theme.colors.primary + '20', 
                          borderRadius: theme.borderRadius.sm,
                          fontSize: theme.typography.fontSize.xs,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}>
                          {opt}
                          <button
                            type="button"
                            onClick={() => {
                              setNewCustomField(prev => ({
                                ...prev,
                                options: prev.options.filter((_, i) => i !== idx)
                              }));
                            }}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              color: theme.colors.error,
                              fontSize: '12px'
                            }}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <Label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', whiteSpace: 'nowrap' }}>
                    <input
                      type="checkbox"
                      checked={newCustomField.required}
                      onChange={(e) => setNewCustomField(prev => ({ ...prev, required: e.target.checked }))}
                    />
                    Required
                  </Label>
                  <Button
                    onClick={() => {
                      if (newCustomField.label && newCustomField.name) {
                        if (newCustomField.type === 'select' && (!newCustomField.options || newCustomField.options.length === 0)) {
                          toast.error('Please add at least one option for dropdown field');
                          return;
                        }
                        setSurveyData(prev => ({
                          ...prev,
                          custom_respondent_fields: [...(prev.custom_respondent_fields || []), { ...newCustomField }]
                        }));
                        setNewCustomField({ name: '', label: '', type: 'text', required: false, options: [] });
                        setNewOption('');
                      }
                    }}
                  >
                    Add Field
                  </Button>
                </div>
                
                {surveyData.custom_respondent_fields?.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {surveyData.custom_respondent_fields.map((field, index) => (
                      <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem', background: theme.colors.background.secondary, borderRadius: theme.borderRadius.md }}>
                        <span style={{ flex: 1, fontWeight: theme.typography.fontWeight.medium }}>{field.label}</span>
                        <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>{field.type}</span>
                        {field.required && <span style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.error }}>Required</span>}
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<X size={14} />}
                          onClick={() => {
                            setSurveyData(prev => ({
                              ...prev,
                              custom_respondent_fields: prev.custom_respondent_fields.filter((_, i) => i !== index)
                            }));
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
          <Button variant="primary" onClick={handleSaveSurvey}>
            <Save size={16} style={{ marginRight: '0.5rem' }} />
            {editingSurvey ? 'Update Survey' : 'Create Survey'}
          </Button>
          <Button variant="ghost" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
        </div>

        {/* Questions Section */}
        {editingSurvey && currentSurvey && (
          <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: `2px solid ${theme.colors.primary}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: theme.typography.fontSize.xl, fontWeight: theme.typography.fontWeight.semibold }}>
                  Survey Questions ({currentSurvey.questions?.length || 0})
                </h3>
                <p style={{ margin: '0.25rem 0 0 0', fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                  Add questions to build your survey. Questions are displayed in the order they appear.
                </p>
              </div>
              <Button
                variant="primary"
                leftIcon={<PlusCircle size={18} />}
                onClick={() => openQuestionModal()}
                style={{ minWidth: '140px' }}
              >
                Add Question
              </Button>
            </div>

            <QuestionsList>
              {currentSurvey.questions?.map((question, index) => (
                <QuestionItem key={question.id} theme={theme}>
                  <GripVertical size={20} style={{ opacity: 0.5, cursor: 'grab' }} />
                  <QuestionContent>
                    <QuestionText theme={theme}>
                      {index + 1}. {question.question_text}
                      {question.is_required && <span style={{ color: theme.colors.error, marginLeft: '0.5rem' }}>*</span>}
                    </QuestionText>
                    <QuestionMeta theme={theme}>
                      Type: {question.question_type}
                      {question.options && question.options.length > 0 && (
                        <OptionsList>
                          Options: {question.options.join(', ')}
                        </OptionsList>
                      )}
                    </QuestionMeta>
                  </QuestionContent>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Button
                      variant="ghost"
                      size="sm"
                      leftIcon={<Edit size={14} />}
                      onClick={() => openQuestionModal(question)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      leftIcon={<Trash2 size={14} />}
                      onClick={() => handleDeleteQuestion(question.id)}
                    />
                  </div>
                </QuestionItem>
              ))}
              {(!currentSurvey.questions || currentSurvey.questions.length === 0) && (
                <div style={{ textAlign: 'center', padding: '2rem', color: theme.colors.text.secondary }}>
                  No questions yet. Click "Add Question" to start building your survey.
                </div>
              )}
            </QuestionsList>
          </div>
        )}
      </Modal>

      {/* Question Form Modal */}
      <Modal
        isOpen={showQuestionModal}
        onClose={() => {
          setShowQuestionModal(false);
          setEditingQuestion(null);
        }}
        title={editingQuestion ? 'Edit Question' : 'Add Question'}
        size="medium"
      >
        <FormGroup theme={theme}>
          <Label theme={theme}>Question Text *</Label>
          <Textarea
            value={questionData.question_text}
            onChange={(e) => setQuestionData(prev => ({ ...prev, question_text: e.target.value }))}
            placeholder="Enter your question"
            rows={3}
            required
          />
        </FormGroup>

        <FormGroup theme={theme}>
          <Label theme={theme}>Question Type *</Label>
          <Select
            theme={theme}
            value={questionData.question_type}
            onChange={(e) => setQuestionData(prev => ({ ...prev, question_type: e.target.value, options: e.target.value === 'rating' ? ['1', '2', '3', '4', '5'] : [] }))}
          >
            <optgroup label="Text Questions">
              <option value="text">Short Text Answer</option>
              <option value="textarea">Paragraph / Long Text Answer</option>
            </optgroup>
            <optgroup label="Choice Questions">
              <option value="radio">Single Choice (Radio Buttons)</option>
              <option value="checkbox">Multiple Choice (Checkboxes)</option>
              <option value="select">Dropdown / Select Menu</option>
            </optgroup>
            <optgroup label="Special Input Types">
              <option value="number">Number Input</option>
              <option value="email">Email Address</option>
              <option value="date">Date Picker</option>
              <option value="rating">Rating Scale (1-5 Stars)</option>
            </optgroup>
            <optgroup label="Advanced">
              <option value="file">File Upload</option>
              <option value="matrix">Matrix / Grid Question</option>
            </optgroup>
          </Select>
          <div style={{ marginTop: '0.5rem', fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>
            {questionData.question_type === 'radio' && 'User can select one option'}
            {questionData.question_type === 'checkbox' && 'User can select multiple options'}
            {questionData.question_type === 'select' && 'User selects from a dropdown menu'}
            {questionData.question_type === 'rating' && 'User rates on a scale (you can customize the scale in options)'}
            {questionData.question_type === 'matrix' && 'Advanced: Create a grid of questions with shared answer options'}
          </div>
        </FormGroup>

        {(questionData.question_type === 'radio' || 
          questionData.question_type === 'checkbox' || 
          questionData.question_type === 'select' ||
          questionData.question_type === 'rating' ||
          questionData.question_type === 'matrix') && (
          <FormGroup theme={theme}>
            <Label theme={theme}>Options</Label>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <Input
                value={questionOption}
                onChange={(e) => setQuestionOption(e.target.value)}
                placeholder={questionData.question_type === 'rating' ? "Enter rating value (e.g., 1, 2, 3...)" : "Add option"}
                onKeyPress={(e) => e.key === 'Enter' && addOption()}
              />
              <Button onClick={addOption}>Add</Button>
            </div>
            {questionData.question_type === 'rating' && (
              <div style={{ marginBottom: '0.5rem', padding: '0.75rem', background: theme.colors.background.secondary, borderRadius: theme.borderRadius.md, fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>
                <strong>Tip:</strong> For rating scales, add options like "1", "2", "3", "4", "5" or "Poor", "Fair", "Good", "Very Good", "Excellent"
              </div>
            )}
            {questionData.question_type === 'matrix' && (
              <div style={{ marginBottom: '0.5rem', padding: '0.75rem', background: theme.colors.background.secondary, borderRadius: theme.borderRadius.md, fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>
                <strong>Tip:</strong> For matrix questions, add the column options here. The rows will be created as separate questions with the same options.
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {questionData.options?.map((option, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem', background: theme.colors.background.secondary, borderRadius: theme.borderRadius.md }}>
                  <span style={{ flex: 1 }}>{option}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<X size={14} />}
                    onClick={() => removeOption(index)}
                  />
                </div>
              ))}
            </div>
          </FormGroup>
        )}

        <FormGroup theme={theme}>
          <Label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={questionData.is_required}
              onChange={(e) => setQuestionData(prev => ({ ...prev, is_required: e.target.checked }))}
            />
            Required Question
          </Label>
        </FormGroup>

        <FormGroup theme={theme}>
          <Label theme={theme}>Help Text</Label>
          <Input
            value={questionData.help_text}
            onChange={(e) => setQuestionData(prev => ({ ...prev, help_text: e.target.value }))}
            placeholder="Additional help text for users"
          />
        </FormGroup>

        <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
          <Button variant="primary" onClick={editingQuestion ? handleUpdateQuestion : handleAddQuestion}>
            <Save size={16} style={{ marginRight: '0.5rem' }} />
            {editingQuestion ? 'Update Question' : 'Add Question'}
          </Button>
          <Button variant="ghost" onClick={() => setShowQuestionModal(false)}>
            Cancel
          </Button>
        </div>
      </Modal>
    </Container>
  );
};

export default SurveyManagement;

